import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Save } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { haptic } from '@/lib/telegram';
import { useI18n } from '@/i18n';
import { Notice, Spinner } from './ui';
import type { PixProfile } from '@/types/api';

const INPUT_MODE: Record<string, 'text' | 'numeric' | 'email' | 'tel'> = { cpf: 'numeric', email: 'email', phone: 'tel', birthdate: 'numeric' };

function formatCpfInput(v: string): string {
    const d = v.replace(/\D/g, '').slice(0, 11);
    return d.replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2');
}
function formatDateInput(v: string): string {
    const d = v.replace(/\D/g, '').slice(0, 8);
    return d.replace(/^(\d{2})(\d)/, '$1/$2').replace(/^(\d{2})\/(\d{2})(\d)/, '$1/$2/$3');
}

/**
 * PIX payer details (FulgurFix): asked once, used by PIX deposits and paid to by PIX withdrawals.
 * `fields` = the fields to show (the deposit's or the withdrawal's needs, or all asked ones to edit).
 */
export function PixProfileForm({ profile, fields, onSaved, intro }: { profile: PixProfile; fields: string[]; onSaved: (p: PixProfile) => void; intro?: string }) {
    const { t, te } = useI18n();
    const qc = useQueryClient();
    const [values, setValues] = useState<Record<string, string>>(() => {
        const p = profile.profile as unknown as Record<string, string>;
        return Object.fromEntries(fields.map(f => [f, p[f] || '']));
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const set = (f: string, v: string) => setValues(s => ({ ...s, [f]: f === 'cpf' ? formatCpfInput(v) : f === 'birthdate' ? formatDateInput(v) : v }));

    const save = async () => {
        const missing = fields.find(f => !String(values[f] || '').trim());
        if (missing) { setError(t('pix.fillField', { field: t(`pix.fields.${missing}`) })); return; }
        setBusy(true); setError(null);
        try {
            const { data } = await api.post<PixProfile>('/pix/profile', values);
            qc.setQueryData(['pix', 'profile'], data);
            haptic('success');
            onSaved(data);
        } catch (e) {
            const err = apiError(e);
            setError(err.error === 'profile_invalid' && typeof err.field === 'string' ? t(`pix.invalid.${err.field}`) : te(err));
            haptic('error');
        } finally { setBusy(false); }
    };

    return (
        <div className="space-y-3">
            <Notice>{intro || t('pix.profileIntro')}</Notice>
            {fields.map(f => (
                <div key={f}>
                    <label className="label">{t(`pix.fields.${f}`)}</label>
                    <input className="input" inputMode={INPUT_MODE[f] || 'text'} autoComplete="off" placeholder={t(`pix.placeholders.${f}`)}
                           value={values[f] || ''} onChange={e => set(f, e.target.value)} />
                </div>
            ))}
            {error && <Notice tone="warn">{error}</Notice>}
            <button className="btn-primary w-full" disabled={busy} onClick={save}>
                {busy ? <Spinner className="w-4 h-4" /> : <Save className="w-4 h-4" />} {t('pix.save')}
            </button>
        </div>
    );
}
